import { BaseComponent } from '../../base-component';
import { DIMENSION_SELECTED_TOPIC, LAST_SELECTED_VARIATION_ID_COOKIE_NAME, RELOAD_COMPONENT_TOPIC } from '../../constants';
import { DimensionsStateEvent, DimensionType, DimensionValueByType } from '../../dimension-selection/shared-types';
import { initializeOProduct } from '../../_common/client/product.const';
import { waitForCondition } from '../../_common/client/wait-for-condition';
import { generateEventMergeId, setGlobalEventMergeId, trackView } from '../../_common/client/tracking';
import { createStatusCodeErrorMessage, redirectToPreviousVariation } from './ui-aggregator-redirect';
import { cookie } from '@otto-ec/global-resources/cookie';
import { lockInit } from '@otto-ec/global-resources/tracking';
import { startSpan } from '@otto-ec/global-resources/apm';

type SeoRedirectTrackingData = { product_SeoRedirect?: string; product_SeoRedirectOrigin?: string };

export class UiAggregatorComponent extends BaseComponent {
    public rootSelector = '.js_pdp_ui-aggregator';
    protected initialReferrer = '';
    private isLocked = false;
    private isRedirectToggleActive = false;
    private isErrorPageToggleActive = false;

    private async lockExecution(action: () => Promise<void>) {
        if (this.isLocked) {
            return;
        }

        this.isLocked = true;
        await action().finally(() => {
            this.isLocked = false;
        });
    }

    public async init(): Promise<void> {
        this.initialReferrer = document.referrer;
        this.isRedirectToggleActive = this.getRootElement().dataset.shouldRedirect === 'true';
        this.isErrorPageToggleActive = this.getRootElement().dataset.shouldShowErrorPage === 'true';

        initializeOProduct();

        await this.waitPageJsIsLoaded();
        await this.trackSeoRedirect();

        this.registerDimensionChangedEventHandler();

        this.setEnteredArticleNumberAndPromotionToOProduct();

        this.setTrackingEventMergeIdToOProduct(generateEventMergeId());

        this.writeVariationIdToCookie();

        await this.emitComponentsHtmlLoadedEvent();
    }

    public async onHtmlLoaded(): Promise<void> {
        return;
    }

    private async trackSeoRedirect() {
        const seoTrackingData = this.getAndRemoveSeoRedirectFromFragment();
        if (seoTrackingData.product_SeoRedirect || seoTrackingData.product_SeoRedirectOrigin) {
            trackView(seoTrackingData);
        }
    }

    private writeVariationIdToCookie() {
        const variationId = new URLSearchParams(window.location.search).get('variationId') || this.getRootElement().dataset.variationId;
        if (variationId) {
            cookie.set(LAST_SELECTED_VARIATION_ID_COOKIE_NAME, variationId);
        }
    }

    private getAndRemoveSeoRedirectFromFragment(): SeoRedirectTrackingData {
        const fragment = o_util.fragment.deserialize<{ seoRedirect?: string; seoRedirectOrigin?: string }>(false);

        const seoTrackingData: SeoRedirectTrackingData = {
            product_SeoRedirect: fragment.seoRedirect,
            product_SeoRedirectOrigin: fragment.seoRedirectOrigin,
        };

        delete fragment.seoRedirectOrigin;
        delete fragment.seoRedirect;
        window.location.hash = o_util.fragment.serialize(fragment) ?? '';

        return seoTrackingData;
    }

    private updateURL(variationId: string): void {
        const fragment = o_util.fragment.deserialize() || '';

        const serializedFragment = o_util.fragment.serialize(fragment);

        const searchParams = new URLSearchParams(window.location.search);
        variationId && searchParams.set('variationId', variationId);
        const searchString = searchParams.toString() ? `?${searchParams.toString()}` : '';
        window.history.replaceState(null, '', `${window.location.pathname}${searchString}${serializedFragment}`);
    }

    /*                        */
    private setEnteredArticleNumberAndPromotionToOProduct() {
        const url = new URL(window.location.href);
        const articleNumberAndPromotion = url.searchParams.get('artNr');
        if (articleNumberAndPromotion) {
            o_product.data.enteredArticleNumberAndPromotion = `${articleNumberAndPromotion}`; /*                                                                                                                                                                            */
            url.searchParams.delete('artNr');
        }
        window.history.replaceState(window.history.state, '', url);
    }

    /**
 *
 *
 *
 */
    protected setTrackingEventMergeIdToOProduct(eventMergeId?: string): void {
        setGlobalEventMergeId(eventMergeId);
    }

    private destroyObsleteComponentsReloadCallbacks(): void {
        window?.o_product.reloadCallbacks?.forEach(callback => {
            o_global.eventQBus.off(RELOAD_COMPONENT_TOPIC, callback);
        });
        if (window?.o_product?.reloadCallbacks) {
            o_product.reloadCallbacks = [];
        }
    }

    /*                                                       */
    protected registerDimensionChangedEventHandler(): void {
        o_global.eventQBus.on(DIMENSION_SELECTED_TOPIC, async (data: DimensionsStateEvent) => {
            await this.lockExecution(async () => {
                const currentVariationId = this.getRenderedVariationId();
                const dimensionsState = data.dimensionStates;
                const selectedState = data.selectedState;
                await this.renderReloadableComponents(component => this.renderComponent(component, currentVariationId, dimensionsState));

                this.setVariationChangedGlobalState();

                const variationIdAfterRerendering = this.getRenderedVariationId();
                this.updateURL(variationIdAfterRerendering);

                this.setTrackingEventMergeIdToOProduct(generateEventMergeId());

                this.trackDimensionChange(selectedState, data.dimensionTypeUsed, data.dimensionEnabled);

                this.writeVariationIdToCookie();

                this.emitComponentsHtmlLoadedEvent();
                this.destroyObsleteComponentsReloadCallbacks();
            });
        });
    }

    protected getRenderedVariationId(): string {
        const currentVariationId = document.querySelector<HTMLElement>(this.rootSelector)?.dataset.variationId;

        if (!currentVariationId) {
            throw new Error('can not find variation id');
        }

        return currentVariationId;
    }

    private static readonly SDA_VARIATION_RELOAD_TOPIC = 'as.odin.sda.variationReload';

    /**
 *
 */
    protected async emitComponentsHtmlLoadedEvent(): Promise<void> {
        o_global.eventQBus.emit(UiAggregatorComponent.SDA_VARIATION_RELOAD_TOPIC, {
            isVariationChange: !!o_product?.state?.isVariationChanged,
            eventMergeId: o_product.tracking.eventMergeId,
        });
        const unlockTracking = await lockInit(RELOAD_COMPONENT_TOPIC);
        o_global.eventQBus.emit(RELOAD_COMPONENT_TOPIC, {});
        unlockTracking();
    }

    /**
 *
 */
    protected getReloadableComponents(): Array<HTMLElement> {
        const nodeListOfElements = document.querySelectorAll<HTMLElement>('div[data-pdp-ui-aggregator-component]');
        return Array.from(nodeListOfElements);
    }

    /**
 *
 */
    protected setComponentHtmlContent(component: HTMLElement, htmlString: string): void {
        if (!component.parentNode) {
            console.warn('Cannot update outerHTML of component because the component has no parent.');
            return;
        }
        component.outerHTML = htmlString;
    }

    protected removeComponentContent(component: HTMLElement): void {
        if (component.parentNode) {
            this.setComponentHtmlContent(component, '');
        } else {
            console.warn("couldn't remove html content from component because the component had no parent");
        }
    }

    protected async renderReloadableComponents(componentMap: (component: HTMLElement) => Promise<void>): Promise<void> {
        const reloadableComponents = this.getReloadableComponents();
        const renderPromises = reloadableComponents.map(componentMap);
        await Promise.all(renderPromises);
    }

    /**
 *
 *
 *
 *
 */
    protected async renderComponent(
        component: HTMLElement,
        currentVariation: string,
        dimensionsState: DimensionValueByType[]
    ): Promise<void> {
        try {
            const offerId = this.getRootElement().getAttribute('data-offer-id');
            const productId = this.getRootElement().getAttribute('data-product-id');
            const offerOrProductId = (offerId || productId) as string;
            const fetchUrl = this.getComponentFetchUrl(component, currentVariation, offerOrProductId);
            let response = await this.fetchComponentHtml(fetchUrl, dimensionsState);

            if (response.status === 401 && this.isRedirectToggleActive) {
                this.logError({ message: createStatusCodeErrorMessage(response.status) });
                redirectToPreviousVariation(currentVariation);
                return;
            }

            if (response.status === 503 && this.isErrorPageToggleActive) {
                response = await this.fetchErrorPage();
            }

            const componentHtml = await response.text();
            this.setComponentHtmlContent(component, componentHtml);
        } catch (e) {
            this.logError({
                error: e as Error,
                data: {
                    currentVariation: currentVariation,
                    dimensionsState: JSON.stringify(dimensionsState),
                },
            });
            this.removeComponentContent(component);
        }
    }

    private async fetchErrorPage(): Promise<Response> {
        return await fetch(`${window.location.origin}/static-proxy/product-assets/error.html`);
    }

    /**
 *
 */
    protected async fetchComponentHtml(fetchUrl: string, dimensionsState: DimensionValueByType[]): Promise<Response> {
        const awsWafIntegration = window.AwsWafIntegration !== undefined;
        const span = await startSpan('fetch component html', 'resource.component.fetch', { blocking: true });
        span?.addLabels({ team: 'ft5', service: 'component-repository', aws_waf_integration: awsWafIntegration });

        const [sessionId, otp] = this.calculateOneTimePad();

        const fetchFn = awsWafIntegration ? window.AwsWafIntegration.fetch : fetch;

        const result = await fetchFn(fetchUrl, {
            method: 'POST',
            body: JSON.stringify({ dimensions: dimensionsState }),
            headers: {
                'initial-referrer': this.initialReferrer,
                'content-type': 'application/json',
                'ft5-pdp-otp': otp ?? '',
                'ft5-pdp-session': sessionId ?? '',
                'x-requested-with': 'XMLHttpRequest',
            },
        });
        span?.end();
        return result;
    }

    private calculateOneTimePad(): [string | undefined, string | undefined] {
        const operand1Element = document.querySelector('input[name="ft5-pdp-op1"]') as HTMLInputElement;
        const operand2Element = document.querySelector('input[name="ft5-pdp-op2"]') as HTMLInputElement;
        const sessionIdElement = document.querySelector('input[name="ft5-pdp-session"]') as HTMLInputElement;

        if (!operand1Element || !operand2Element || !sessionIdElement || typeof BigInt === 'undefined') {
            return [undefined, undefined];
        }

        const otpOperand1 = BigInt(`0x${operand1Element.value}`);
        const otpOperand2 = BigInt(`0x${operand2Element.value}`);
        const otp = otpOperand1 ^ otpOperand2;

        return [sessionIdElement.value, otp.toString(16)];
    }

    /**
 *
 */
    protected getComponentFetchUrl(component: HTMLElement, variationId: string, offerOrProductId: string): string {
        const componentName = component.dataset.pdpUiAggregatorComponent;
        const params = new URLSearchParams({ variationId });

        if (!componentName) {
            throw new Error(`cant read component name from html data: "${component.outerHTML}"`);
        }
        return `/dcs/${componentName}/${offerOrProductId}?${params}`;
    }

    /**
 *
 */
    private async waitPageJsIsLoaded(): Promise<void> {
        await waitForCondition(() => document.readyState !== 'loading');
    }

    /**
 *
 */
    private setVariationChangedGlobalState(value = true): void {
        window.o_product.state.isVariationChanged = value;
    }

    private trackDimensionChange(
        selectedState: DimensionValueByType[],
        dimensionTypeUsed?: DimensionType,
        dimensionEnabled?: boolean
    ): void {
        this.trackView({
            product_Dimension: selectedState.map(state => state.type).join('_'),
            ...(dimensionTypeUsed ? { product_DimensionTypeUsed: dimensionTypeUsed } : {}),
            ...(dimensionEnabled !== undefined ? { product_DimensionEnabled: String(dimensionEnabled) } : {}),
        });
    }
}
